import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  DocumentSharingControllerService, UserRef, ProfileProviderPJ, ProfileProviderPjControllerService, DocumentSharing,
  ResourceDemand, ServiceOffering, ServiceDemand, ServiceOfferingControllerService, ResourceDemandControllerService, ServiceDemandControllerService,
  PlanStatusInfo,
  NegotiationControllerService,
  Negotiation,
  ProfileProviderPF,
  ResourceOfferingControllerService,
  ResourceOffering
} from '../../core/generated';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { MaterialModule } from '../../shared/material/material.module';
import { ActivatedRoute } from '@angular/router';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ProfileProviderPJCacheListService } from '../../shared/services/profile-provider-pj-cache-list.service';
import { UserRefCacheListService } from '../../shared/services/user-ref-cache-list.service';
import { ProviderPJContextService } from '../../shared/services/provider-pj-context.service';
import { PlanStatusService } from '../../shared/services/plan-status-cache.service';
import { Feature } from '../../shared/config/package-features.config';
import { EncryptionUtil } from '../../shared/utils/encryption.util';
import { ProfileProviderPFCacheListService } from '../../shared/services/profile-provider-pf-cache-list.service';
import { ContainerComponent } from '../../shared/container/container.component';
import { MatDialog } from '@angular/material/dialog';
import { DocumentListDialogComponent } from '../../shared/document-list-dialog/document-list-dialog.component';

@Component({
    selector: 'app-dashboard-provider-pj',
    standalone: true,
    imports: [MaterialModule, CommonModule, ContainerComponent],
    templateUrl: './dashboard-provider-pj.component.html',
    styleUrls: ['./dashboard-provider-pj.component.scss']
})
export class DashboardProviderPJComponent implements OnInit, OnDestroy {
  userRefCacheListService = inject(UserRefCacheListService);
  profileProviderPJService = inject(ProfileProviderPjControllerService);
  UserPreferencesCacheService = inject(UserPreferencesCacheService);
  deviceService = inject(DeviceDetectorService);
  route = inject(ActivatedRoute);
  providerPJContextService = inject(ProviderPJContextService);
  documentSharingService = inject(DocumentSharingControllerService);
  resourceOfferingService = inject(ResourceOfferingControllerService);
  serviceOfferingService = inject(ServiceOfferingControllerService);
  resourceDemandService = inject(ResourceDemandControllerService);
  serviceDemandService = inject(ServiceDemandControllerService);
  planStatusService = inject(PlanStatusService);
  negotiationService = inject(NegotiationControllerService);
  private readonly profileProviderPFCacheListService = inject(ProfileProviderPFCacheListService);
  private readonly profileProviderPJCacheListService = inject(ProfileProviderPJCacheListService);
  private readonly dialog = inject(MatDialog);

  isMobile = false;
  user!: UserRef;
  profileProviderPJ!: ProfileProviderPJ;

  documentSharing: DocumentSharing[] = [];
  documentSharingReceived: DocumentSharing[] = [];
  resourceOfferingList: ResourceOffering[] = [];
  serviceOfferingList: ServiceOffering[] = [];
  resourceDemandList: ResourceDemand[] = [];
  serviceDemandList: ServiceDemand[] = [];
  allPlanStatuses: PlanStatusInfo[] = [];
  planStatusMap: { [cpfCnpj: string]: PlanStatusInfo } = {};

  usersCache: Map<string, string> = new Map();
  profilePFCache: Map<string, ProfileProviderPF> = new Map();

  isLoadingProfile = false;

  negotiations: Negotiation[] = [];
  readonly Feature = Feature;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    this.isMobile = this.deviceService.isMobile();

    this.planStatusService.planStatuses$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
      this.allPlanStatuses = list || [];
      this.planStatusMap = {};
      this.allPlanStatuses.forEach(ps => {
        if (ps && ps.cpfCnpj) {
          this.planStatusMap[ps.cpfCnpj] = ps;
        }
      });
    });

    this.user = await firstValueFrom(this.UserPreferencesCacheService.getUser());

    await this.planStatusService.loadForCurrentUser();

    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (params) => {
        const id = params.get('id');
        if (id) {
          this.isLoadingProfile = true;
          try {
            const profile = await firstValueFrom(this.profileProviderPJService.findById6(Number(id)));
            if (profile) {
              this.profileProviderPJ = profile;

              this.providerPJContextService.setProviderId(id);
              this.providerPJContextService.setProviderPJ(profile);

              await this.loadDocumentSharingData();
              await this.loadResourceOfferingList(profile.cnpj!);
              await this.loadServiceOfferingList(profile.cnpj!);
              await this.loadResourceDemandList(profile.cnpj!);
              await this.loadServiceDemandList(profile.cnpj!);
              await this.loadNegotiations(profile.cnpj!);

              this.planStatusService.refresh().catch(err => console.error('Error refreshing plan statuses', err));
            }
          } finally {
            this.isLoadingProfile = false;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadDocumentSharingData(): Promise<void> {
    if (!this.profileProviderPJ?.cnpj) return;

    const [sharingData, receivedData] = await Promise.all([
      firstValueFrom(this.documentSharingService.getGrantedTo(this.profileProviderPJ.cnpj)),
      firstValueFrom(this.documentSharingService.getGrantedFrom(this.profileProviderPJ.cnpj))
    ]);

    this.documentSharing = (sharingData as DocumentSharing[]) || [];
    this.documentSharingReceived = (receivedData as DocumentSharing[]) || [];

    this.preloadUsersCache();
  }

  private preloadUsersCache(): void {
    this.usersCache.clear();
    this.profilePFCache.clear();

    const cpfsToLoad = new Set<string>();

    this.documentSharing.forEach(item => {
      if (item.ownerDocumentsCpfCnpj && item.ownerDocumentsCpfCnpj.length === 11) {
        cpfsToLoad.add(item.ownerDocumentsCpfCnpj);
      }
    });

    this.documentSharingReceived.forEach(item => {
      if (item.ownerDocumentsCpfCnpj && item.ownerDocumentsCpfCnpj.length === 11) {
        cpfsToLoad.add(item.ownerDocumentsCpfCnpj);
      }
    });

    cpfsToLoad.forEach(cpf => {
      // Carrega o perfil PF de forma assíncrona e armazena no cache
      this.profileProviderPFCacheListService.findProfileProviderPFByCpf(cpf, (profilePF) => {
        if (profilePF) {
          this.profilePFCache.set(cpf, profilePF);
          this.usersCache.set(cpf, profilePF.name || 'Nome não disponível');
        } else {
          this.usersCache.set(cpf, 'Usuário não encontrado');
        }
      });
    });
  }

  async loadResourceOfferingList(cnpj: string): Promise<void> {
    const response = await firstValueFrom(this.resourceOfferingService.findBycnpj(cnpj));
    this.resourceOfferingList = (response as ResourceOffering[]) || [];
  }

  async loadResourceDemandList(cnpj: string): Promise<void> {
    const response = await firstValueFrom(this.resourceDemandService.findBycnpj1(cnpj));
    this.resourceDemandList = (response as ResourceDemand[]) || [];
  }

  async loadServiceOfferingList(cnpj: string): Promise<void> {
    const response = await firstValueFrom(this.serviceOfferingService.findByCnpj(cnpj));
    this.serviceOfferingList = (response as ServiceOffering[]) || [];
  }

  async loadServiceDemandList(cnpj: string): Promise<void> {
    const response = await firstValueFrom(this.serviceDemandService.findByCnpj1(cnpj));
    this.serviceDemandList = (response as ServiceDemand[]) || [];
  }

  async loadNegotiations(cnpj: string): Promise<void> {
    const response = await firstValueFrom(this.negotiationService.getNegotiationsByParticipant(cnpj));
    this.negotiations = (response as Negotiation[]) || [];
  }

  getCompany(cnpj: string): ProfileProviderPJ {
    if (!cnpj) return { companyName: 'CNPJ inválido' } as ProfileProviderPJ;

    const pj = this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(cnpj);
    return pj || { companyName: 'Empresa não encontrada' } as ProfileProviderPJ;
  }

  getUser(cpf: string): string {
    if (!cpf) return 'CPF inválido';

    if (this.usersCache.has(cpf)) {
      return this.usersCache.get(cpf)!;
    }

    return 'Carregando...';
  }
  
  findPlanStatusBycpfCnpj(cpfCnpj: string): PlanStatusInfo | null {
    if (!cpfCnpj) {
      return null;
    }
    return this.planStatusMap[cpfCnpj] || null;
  }

  canAccess(cpfCnpj: string | undefined | null, feature?: Feature | Feature[]): boolean {
    if (!cpfCnpj) return false;
    return this.planStatusService.canAccessFeature(cpfCnpj, feature).hasAccess;
  }

  hasFeatureAccess(cpfCnpj: string | undefined | null, feature: Feature | Feature[]): boolean {
    if (!cpfCnpj) return false;
    return this.planStatusService.hasFeatureAccess(cpfCnpj, feature);
  }

  hasAnyActivePlan(): boolean {
    return this.planStatusService.hasAnyActivePlan();
  }

  isProvider(cpfCnpj: string): boolean {
    return this.planStatusService.isProvider(cpfCnpj);
  }

  isContractor(cpfCnpj: string): boolean {
    return this.planStatusService.isContractor(cpfCnpj);
  }

  encriptCpfcnpj(cpfcnpj: string): any | string {
    return EncryptionUtil.encrypt(cpfcnpj);
  }

  getCompanyDto(cnpj: string): ProfileProviderPJ {
    const pj =
      this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(
        cnpj
      );
    return pj ? pj : { companyName: 'N/A' } as ProfileProviderPJ;
  }

  getProfilePFDto(cpf: string): ProfileProviderPF {
    if (!cpf) {
      return { firstName: 'CPF inválido' } as ProfileProviderPF;
    }
    
    // Retorna do cache local se disponível
    if (this.profilePFCache.has(cpf)) {
      return this.profilePFCache.get(cpf)!;
    }
    
    // Retorna um placeholder enquanto carrega
    return { firstName: 'Carregando...', name: 'Carregando...' } as ProfileProviderPF;
  }

  getParticipantDtoName(negotiation: Negotiation): string | undefined {
    const otherCpfCnpj = negotiation.initiatorCpfCnpj === this.user.cpf
      ? negotiation.recipientCpfCnpj
      : negotiation.initiatorCpfCnpj;
    if (otherCpfCnpj) {
      if (otherCpfCnpj.length === 11) {
        const profilePFDto = this.getProfilePFDto(otherCpfCnpj);
        return profilePFDto.name;
      } else if (otherCpfCnpj.length === 14) {
        const companyDto = this.getCompanyDto(otherCpfCnpj);
        return companyDto.companyName;
      }
    }
    return undefined;
  }

  getNegotiationStatusDescription(status: string | undefined): string {
    if (!status) return 'Status não informado';
    
    const statusMap: { [key: string]: string } = {
      'PENDING': 'Pendente',
      'ACCEPTED': 'Aceito',
      'REJECTED': 'Rejeitado',
      'CANCELLED': 'Cancelado',
      'COMPLETED': 'Concluído'
    };
    
    return statusMap[status] || status;
  }

  get totalDocuments(): number {
    return (this.documentSharing?.length || 0) + (this.documentSharingReceived?.length || 0);
  }

  viewDocuments(documentSharing: DocumentSharing): void {
    this.dialog.open(DocumentListDialogComponent, {
      width: '600px',
      data: {
        ownerDocumentsCpfCnpj: documentSharing.ownerDocumentsCpfCnpj,
        grantedCompanyCnpj: documentSharing.grantedCompanyCnpj,
      }
    });
  }
}
